import { apiRequest } from '@/api/core/client';
import type {
  Note,
  NoteTagFull,
  NotesBootstrapPayload,
  ActiveSessionResponse,
  NoteTaskLink,
  NoteSearchResult,
  NotesSettings,
} from '@/types/note';

interface CreateNoteInput {
  contentMarkdown?: string;
  title?: string;
  metadata?: Record<string, unknown>;
  /** false creates an open scratchpad session note; defaults to closed. */
  closed?: boolean;
}

interface UpdateNoteInput {
  contentMarkdown?: string;
  title?: string | null;
  metadata?: Record<string, unknown>;
}

interface ListNotesOptions {
  limit?: number;
  offset?: number;
  tagId?: string;
  archived?: boolean;
  deleted?: boolean;
}

interface ListNotesResponse {
  notes: Note[];
  total: number;
  hasMore: boolean;
}

// Bootstrap
export async function fetchNotesBootstrap(): Promise<NotesBootstrapPayload> {
  return apiRequest<NotesBootstrapPayload>({
    path: '/notes/bootstrap',
  });
}

// Notes CRUD
export async function fetchNotes(options?: ListNotesOptions): Promise<ListNotesResponse> {
  const query: Record<string, string> = {};
  if (options?.limit) query.limit = String(options.limit);
  if (options?.offset) query.offset = String(options.offset);
  if (options?.tagId) query.tagId = options.tagId;
  if (options?.archived) query.archived = 'true';
  if (options?.deleted) query.deleted = 'true';

  return apiRequest<ListNotesResponse>({
    path: '/notes',
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

export async function fetchNote(noteId: string): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}`,
  });
  return note;
}

export async function createNote(payload: CreateNoteInput = {}): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: '/notes',
    method: 'POST',
    body: payload,
  });
  return note;
}

export async function updateNote(noteId: string, payload: UpdateNoteInput): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}`,
    method: 'PATCH',
    body: payload,
  });
  return note;
}

export async function deleteNote(noteId: string, hard = false): Promise<{ deleted: boolean; note?: Note }> {
  return apiRequest({
    path: `/notes/${noteId}`,
    method: 'DELETE',
    query: hard ? { hard: 'true' } : undefined,
  });
}

export async function restoreNote(noteId: string): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}/restore`,
    method: 'POST',
  });
  return note;
}

export async function archiveNote(noteId: string): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}/archive`,
    method: 'POST',
  });
  return note;
}

// Lifecycle

/**
 * Close an open session note (graduate it to the grid). Empty notes are
 * deleted instead; the response distinguishes the two.
 */
export async function closeNote(noteId: string): Promise<{ note?: Note; deleted?: boolean }> {
  return apiRequest({
    path: `/notes/${noteId}/close`,
    method: 'POST',
  });
}

/** Refresh the decay clock (fired when a note is opened for viewing/editing). */
export async function touchNote(noteId: string): Promise<void> {
  await apiRequest({
    path: `/notes/${noteId}/touch`,
    method: 'POST',
  });
}

export async function setNotePinned(noteId: string, pinned: boolean): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}`,
    method: 'PATCH',
    body: { pinned },
  });
  return note;
}

// Session
export async function getActiveSession(): Promise<ActiveSessionResponse> {
  return apiRequest<ActiveSessionResponse>({
    path: '/notes/session/active',
  });
}

// Search
export async function searchNotes(
  query: string,
  options?: { limit?: number }
): Promise<{ results: NoteSearchResult[]; query: string }> {
  const queryParams: Record<string, string> = { q: query };
  if (options?.limit) queryParams.limit = String(options.limit);

  return apiRequest({
    path: '/notes/search',
    query: queryParams,
  });
}

// Tags
export async function fetchNoteTags(): Promise<NoteTagFull[]> {
  const { tags } = await apiRequest<{ tags: NoteTagFull[] }>({
    path: '/notes/tags',
  });
  return tags;
}

export async function createNoteTag(payload: { name: string; color?: string | null }): Promise<NoteTagFull> {
  const { tag } = await apiRequest<{ tag: NoteTagFull }>({
    path: '/notes/tags',
    method: 'POST',
    body: payload,
  });
  return tag;
}

export async function updateNoteTag(
  tagId: string,
  payload: { name?: string; color?: string | null }
): Promise<NoteTagFull> {
  const { tag } = await apiRequest<{ tag: NoteTagFull }>({
    path: `/notes/tags/${tagId}`,
    method: 'PATCH',
    body: payload,
  });
  return tag;
}

export async function deleteNoteTag(tagId: string): Promise<{ deleted: boolean }> {
  return apiRequest({
    path: `/notes/tags/${tagId}`,
    method: 'DELETE',
  });
}

// Note-Tag assignments
export async function updateNoteTags(noteId: string, tagIds: string[]): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}/tags`,
    method: 'PATCH',
    body: { tagIds },
  });
  return note;
}

// Note-Task links
export async function fetchNoteTasks(noteId: string): Promise<NoteTaskLink[]> {
  const { tasks } = await apiRequest<{ tasks: NoteTaskLink[] }>({
    path: `/notes/${noteId}/tasks`,
  });
  return tasks;
}

export async function linkNoteTask(
  noteId: string,
  payload: {
    taskId: string;
    source?: string;
    sourceText?: string;
    confidence?: number;
  }
): Promise<NoteTaskLink> {
  const { link } = await apiRequest<{ link: NoteTaskLink }>({
    path: `/notes/${noteId}/tasks`,
    method: 'POST',
    body: payload,
  });
  return link;
}

export async function unlinkNoteTask(noteId: string, taskId: string): Promise<{ deleted: boolean }> {
  return apiRequest({
    path: `/notes/${noteId}/tasks/${taskId}`,
    method: 'DELETE',
  });
}

export async function fetchNotesForTask(taskId: string): Promise<Note[]> {
  const { notes } = await apiRequest<{ notes: Note[] }>({
    path: `/notes/for-task/${taskId}`,
  });
  return notes;
}

// AI Processing

export type { ExtractedTask } from '@/types/note';

export interface NoteAiAnnotation {
  status: 'pending' | 'running' | 'complete' | 'error';
  summary: string | null;
  result: unknown;
  error: string | null;
}

export interface ProcessTitleAndTagsResponse {
  annotation: NoteAiAnnotation | null;
  note: Note;
  newTags?: NoteTagFull[];
}

export interface ExtractTasksResponse {
  annotation: NoteAiAnnotation | null;
}

export async function processNoteTitleAndTags(
  noteId: string,
  options?: { force?: boolean }
): Promise<ProcessTitleAndTagsResponse> {
  return apiRequest<ProcessTitleAndTagsResponse>({
    path: `/notes/${noteId}/ai/process`,
    method: 'POST',
    body: options,
  });
}

export async function extractNoteTasks(
  noteId: string,
  options?: { force?: boolean }
): Promise<ExtractTasksResponse> {
  return apiRequest<ExtractTasksResponse>({
    path: `/notes/${noteId}/ai/extract-tasks`,
    method: 'POST',
    body: options,
  });
}

/** Read the stored extraction annotation without triggering an AI run. */
export async function getNoteTaskExtraction(noteId: string): Promise<ExtractTasksResponse> {
  return apiRequest<ExtractTasksResponse>({
    path: `/notes/${noteId}/ai/extract-tasks`,
  });
}

/** Mark the current task suggestions as handled (confirmed or dismissed). */
export async function resolveTaskSuggestions(noteId: string): Promise<Note> {
  const { note } = await apiRequest<{ note: Note }>({
    path: `/notes/${noteId}/ai/extract-tasks/resolve`,
    method: 'POST',
  });
  return note;
}

// Settings
export async function fetchNotesSettings(): Promise<NotesSettings> {
  const { settings } = await apiRequest<{ settings: NotesSettings }>({
    path: '/notes/settings',
  });
  return settings;
}

export async function updateNotesSettings(updates: Partial<NotesSettings>): Promise<NotesSettings> {
  const { settings } = await apiRequest<{ settings: NotesSettings }>({
    path: '/notes/settings',
    method: 'PATCH',
    body: updates,
  });
  return settings;
}
